import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@/app/store/store";
import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type UserPreferences = {
  role: string | null;
};

export type UpdateUserPreferencesPayload = {
  role?: string;
};

export type UserPreferencesState = {
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  preferences: UserPreferences | null;
};

export const userPreferencesInitialState: UserPreferencesState = {
  status: STATUS.IDLE,
  error: null,
  preferences: null,
};

function isUserPreferences(value: unknown): value is UserPreferences {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return typeof row.role === "string" || row.role === null;
}

export const fetchUserPreferences = createAsyncThunk<
  UserPreferences,
  void,
  { rejectValue: string }
>("userPreferences/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/context-hub/user-preferences", { method: "GET" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserPreferences(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

export const updateUserPreferences = createAsyncThunk<
  UserPreferences,
  UpdateUserPreferencesPayload,
  { rejectValue: string }
>("userPreferences/update", async (payload, { rejectWithValue }) => {
  try {
    const res = await apiFetch("/v1/context-hub/user-preferences", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!isUserPreferences(data)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Request failed";
    return rejectWithValue(message);
  }
});

const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState: userPreferencesInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchUserPreferences.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.preferences = action.payload;
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.error = action.payload ?? "Request failed";
      });
  },
});

export const userPreferencesReducer = userPreferencesSlice.reducer;

export const selectUserPreferences = (state: RootState) => state.userPreferences.preferences;
export const selectUserPreferencesStatus = (state: RootState) => state.userPreferences.status;
